import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider, useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import StudentLogin from "@/pages/auth/StudentLogin";
import Signup from "@/pages/auth/Signup";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";

import Dashboard from "@/pages/Dashboard";
import Lesson from "@/pages/Lesson";
import ProgressPage from "@/pages/ProgressPage";
import PracticePage from "@/pages/PracticePage";
import CurriculumMap from "@/pages/curriculum/CurriculumMap";
import LevelView from "@/pages/curriculum/LevelView";
import CurriculumLesson from "@/pages/curriculum/CurriculumLesson";
import Achievements from "@/pages/curriculum/Achievements";
import MyAssignments from "@/pages/curriculum/MyAssignments";
import SubjectHub from "@/pages/subjects/SubjectHub";
import CursiveAlphabetPreview from "@/pages/_dev/CursiveAlphabetPreview";
import CursiveTraceCheck from "@/pages/_dev/CursiveTraceCheck";
import SubjectView from "@/pages/subjects/SubjectView";
import UniversalLesson from "@/pages/subjects/UniversalLesson";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import FamilyProgress from "@/pages/teacher/FamilyProgress";
import ManageSubscription from "@/pages/ManageSubscription";
import MyPlan from "@/pages/MyPlan";
import EarnWithZoodLeap from "@/pages/EarnWithZoodLeap";
import ResellerHome from "@/pages/reseller/ResellerHome";
import ResellerLanding from "@/pages/ResellerLanding";
import SchoolAdminHome from "@/pages/org/SchoolAdminHome";
import DistrictAdminHome from "@/pages/org/DistrictAdminHome";
import InviteAccept from "@/pages/auth/InviteAccept";
import LessonStudio from "@/pages/lessons/LessonStudio";
import LibraryPage from "@/pages/LibraryPage";
import LessonWorksheet from "@/pages/lessons/LessonWorksheet";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminBulkLessonUpload from "@/pages/admin/AdminBulkLessonUpload";
import HelpPage from "@/pages/HelpPage";
import WelcomeTour from "@/components/WelcomeTour";
import StudentGuide from "@/components/StudentGuide";

import TeacherHome from "@/pages/teacher/TeacherHome";
import StudentProgressView from "@/pages/teacher/StudentProgressView";
import WorksheetGenerator from "@/pages/teacher/WorksheetGenerator";
import AdminHome from "@/pages/admin/AdminHome";
import AdminCurriculum from "@/pages/admin/AdminCurriculum";
import AdminStats from "@/pages/admin/AdminStats";
import CertificatesPage from "@/pages/CertificatesPage";
import { ViewModeProvider } from "@/lib/viewMode";
import PWAInstallBanner from "@/components/PWAInstallBanner";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
      <div className="font-cursive text-3xl text-[#0D9488] animate-pulse">Loading...</div>
    </div>
  );
}

function StudentApp() {
  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navbar />
      <Routes>
        <Route path="/_dev/cursive-alphabet" element={<React.Suspense fallback={null}><CursiveAlphabetPreview /></React.Suspense>} />
        <Route path="/" element={<SubjectHub />} />
        <Route path="/subject/:subjectId" element={<SubjectView />} />
        <Route path="/learn/:lessonId" element={<UniversalLesson />} />
        {/* Writing studio = existing cursive engine (unchanged) */}
        <Route path="/writing" element={<CurriculumMap />} />
        <Route path="/level/:levelId" element={<LevelView />} />
        <Route path="/activity/:activityId" element={<CurriculumLesson />} />
        <Route path="/achievements" element={<Achievements />} />
        <Route path="/certificates" element={<CertificatesPage mode="self" />} />
        <Route path="/assignments" element={<MyAssignments />} />
        {/* legacy a/b/c routes preserved */}
        <Route path="/missions" element={<Dashboard />} />
        <Route path="/lesson/:letterId" element={<Lesson />} />
        <Route path="/practice" element={<PracticePage />} />
        <Route path="/progress" element={<ProgressPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <StudentGuide />
    </div>
  );
}

function TeacherApp() {
  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navbar />
      <Routes>
        <Route path="/" element={<TeacherHome />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/lessons" element={<LessonStudio />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/writing" element={<CurriculumMap />} />
        <Route path="/level/:levelId" element={<LevelView />} />
        <Route path="/activity/:activityId" element={<CurriculumLesson />} />
        <Route path="/worksheet/:lessonId" element={<LessonWorksheet />} />
        <Route path="/students/:studentId" element={<StudentProgressView />} />
        <Route path="/students/:studentId/progress" element={<FamilyProgress />} />
        <Route path="/students/:studentId/certificates" element={<CertificatesPage mode="adult" />} />
        <Route path="/worksheets" element={<WorksheetGenerator />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/subscription" element={<ManageSubscription />} />
        <Route path="/my-plan" element={<MyPlan />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function ResellerApp() {
  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navbar />
      <Routes>
        <Route path="/" element={<ResellerHome />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/subscription" element={<ManageSubscription />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function AdminApp() {
  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navbar />
      <Routes>
        <Route path="/" element={<AdminHome />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/curriculum" element={<AdminCurriculum />} />
        <Route path="/lessons" element={<LessonStudio />} />
        <Route path="/worksheet/:lessonId" element={<LessonWorksheet />} />
        <Route path="/students/:studentId" element={<StudentProgressView />} />
        <Route path="/students/:studentId/progress" element={<FamilyProgress />} />
        <Route path="/students/:studentId/certificates" element={<CertificatesPage mode="adult" />} />
        <Route path="/plans" element={<AdminPlans />} />
        <Route path="/settings" element={<AdminSettings />} />
        <Route path="/admin/bulk-upload" element={<AdminBulkLessonUpload />} />
        <Route path="/stats" element={<AdminStats />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function SchoolAdminApp() {
  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navbar />
      <Routes>
        <Route path="/" element={<SchoolAdminHome />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/students/:studentId" element={<StudentProgressView />} />
        <Route path="/students/:studentId/progress" element={<FamilyProgress />} />
        <Route path="/students/:studentId/certificates" element={<CertificatesPage mode="adult" />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/subscription" element={<ManageSubscription />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function DistrictAdminApp() {
  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navbar />
      <Routes>
        <Route path="/" element={<DistrictAdminHome />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/students/:studentId" element={<StudentProgressView />} />
        <Route path="/students/:studentId/progress" element={<FamilyProgress />} />
        <Route path="/students/:studentId/certificates" element={<CertificatesPage mode="adult" />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/subscription" element={<ManageSubscription />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function PublicApp() {
  return (
    <Routes>
      <Route path="/_dev/cursive-alphabet" element={<CursiveAlphabetPreview />} />
      <Route path="/_dev/cursive-trace-check" element={<CursiveTraceCheck />} />
      <Route path="/" element={<Landing />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/student-login" element={<StudentLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/r/:slug" element={<ResellerLanding />} />
      <Route path="/earn" element={<EarnWithZoodLeap />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Shell() {
  const { user, role, loading } = useAuth();
  const path = window.location.pathname;
  if (path.startsWith("/invite/")) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<InviteAccept />} />
      </Routes>
    );
  }
  if (loading) return <Loader />;
  if (!user) return <PublicApp />;
  let content;
  if (role === "student") content = <StudentApp />;
  else if (role === "teacher" || role === "parent") content = <TeacherApp />;
  else if (role === "reseller") content = <ResellerApp />;
  else if (role === "school_admin") content = <SchoolAdminApp />;
  else if (role === "district_admin") content = <DistrictAdminApp />;
  else if (role === "admin") content = <AdminApp />;
  else content = <PublicApp />;
  return (
    <>
      {content}
      <WelcomeTour />
      <PWAInstallBanner />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ViewModeProvider>
        <AppProvider>
          <Shell />
          <Toaster />
        </AppProvider>
      </ViewModeProvider>
    </BrowserRouter>
  );
}

export default App;
